/**
 * @file The Part cell of one staged row. Consumer: `SignifyFetchJobInputTable`, which hands it the
 * row's proxy so the cell subscribes to that row alone.
 * A pasted row is editable text: the keystroke writes the proxy — `sync: true` is what keeps the
 * caret in place — and blur or Enter commits, where the draft re-checks the no-duplicate invariant
 * and restores the text it was edited from if the edit collides or empties the row.
 * A picked row shows its payload and is not editable: editing the text would desync it from the
 * product it carries. Remove it and pick again instead.
 */

import { FC, useRef } from 'react';
import { Input, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexCol from '@/components/atoms/FlexCol';
import type { SignifyFetchJobDraftInput } from '../index.types';

const { Text } = Typography;

type SignifyFetchJobInputPartCellProps = {
  row: SignifyFetchJobDraftInput;
  onCommitInput: (key: string, previousInput: string) => void;
};

const SignifyFetchJobInputPartCell: FC<SignifyFetchJobInputPartCellProps> = ({ row, onCommitInput }) => {
  const snap = useSnapshot(row, { sync: true });
  const editedFrom = useRef<string>(row.input);

  const handleFocus = () => {
    editedFrom.current = row.input;
  };

  const handleCommit = () => {
    onCommitInput(row.key, editedFrom.current);
    editedFrom.current = row.input;
  };

  if (snap.product) {
    return (
      <FlexCol gap={0}>
        <Text>{snap.product.name}</Text>
        <Text type="secondary">
          {snap.product.commercialCode} | 12NC: {snap.product.code}
        </Text>
      </FlexCol>
    );
  }

  return (
    <Input
      size="small"
      aria-label={`signify-fetch-job-part-${row.key}`}
      value={snap.input}
      onChange={(event) => {
        row.input = event.target.value;
      }}
      onFocus={handleFocus}
      onBlur={handleCommit}
      onPressEnter={handleCommit}
    />
  );
};

export default SignifyFetchJobInputPartCell;
